<template>
  <div class="m-b-15">
    <WdsStack align-center gap-sm justify-start wrap>
      <WdsSelect
        v-model="invoiceStatus.data"
        :placeholder="$t('Invoice status')"
        :list="$options.invoiceStatusOptions"
        multi-select
      >
        <template #select-all-text>
          <WdsText :text="$t('Select all')" />
        </template>
      </WdsSelect>
      <WdsSelect
        v-model="invoiceType.data"
        :placeholder="$t('Invoice type')"
        :list="$options.invoiceTypeOptions"
        multi-select
      >
        <template #select-all-text>
          <WdsText :text="$t('Select all')" />
        </template>
      </WdsSelect>
      <WdsSelect
        v-model="poNumberSelection.data"
        :placeholder="$t('PO number status')"
        :list="$options.poNumberOptions"
        multi-select
      >
        <template #select-all-text>
          <WdsText :text="$t('Select all')" />
        </template>
      </WdsSelect>
      <WdsSelect
        v-if="currenciesOptions.length > 1"
        v-model="selectedCurrenciesObjects"
        :placeholder="$t('All currencies')"
        :list="currenciesOptions"
        multi-select
      >
        <template #select-all-text>
          <WdsText :text="$t('Select all')" />
        </template>
      </WdsSelect>
      <WdsButtonGhost secondary class="tw-h-[38px]" @click="resetFilters">
        <div class="tw-my-auto">
          {{ $t('Reset filters') }}
        </div>
      </WdsButtonGhost>
    </WdsStack>
  </div>
</template>

<script>
import { WdsButtonGhost, WdsSelect, WdsStack, WdsText } from '@worksome/wds-components'
import FeatureFlagged from '@/components/FeatureFlagged.vue'
import { LocalStorageEncoder } from '@/utils/Encoders'

export default {
  invoiceStatusOptions: [
    { label: $t('Unpaid'), value: 'UNPAID' },
    { label: $t('Overdue'), value: 'OVERDUE' },
    { label: $t('Paid'), value: 'PAID' },
    { label: $t('Credited'), value: 'CREDITED' },
  ],
  invoiceTypeOptions: [
    { label: $t('Invoice'), value: 'INVOICE' },
    { label: $t('Credit Note'), value: 'CREDIT_NOTE' },
  ],
  poNumberOptions: [
    { value: 1, label: $t('Has PO Number') },
    { value: 0, label: $t('Missing PO Number') },
  ],
  components: {
    WdsText,
    WdsButtonGhost,
    WdsSelect,
    WdsStack,
    // eslint-disable-next-line vue/no-unused-components -- Baseline eslint ignore please fix if working on this
    FeatureFlagged,
  },
  props: {
    currencies: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isResetting: false,
      invoiceStatus: new LocalStorageEncoder({
        queryPoint: 'invoice-status',
        decoder: this.decodeInvoiceStatusOptionsFromUrl, // get from uri
        filter: (statuses) => statuses.map((status) => status.value), // pushing to uri
        defaultVal: null,
      }),
      invoiceType: new LocalStorageEncoder({
        queryPoint: 'invoice-type',
        decoder: this.decodeInvoiceTypeOptionsFromUrl, // get from uri
        filter: (types) => types.map((type) => type.value), // pushing to uri
        defaultVal: null,
      }),
      poNumberSelection: new LocalStorageEncoder({
        queryPoint: 'po-number',
        decoder: this.decodePoNumberOptionsFromUrl, // get from uri
        filter: (statuses) => statuses.map((status) => status.value), // pushing to uri
        defaultVal: null,
      }),
      selectedCurrenciesCodes: new LocalStorageEncoder({
        storageKey: 'currencies',
        decoder: (data) => data?.split(',') || [],
        defaultVal: [],
      }),
    }
  },
  computed: {
    filter() {
      return {
        invoiceStatus: this.invoiceStatus.data.map((status) => status.value),
        invoiceType: this.invoiceType.data.map((type) => type.value),
        hasPoNumber: this.hasPoNumber,
        currencies: this.selectedCurrenciesCodes.data.filter((value) =>
          this.currenciesOptions.map((currency) => currency.value).includes(value),
        ),
      }
    },
    hasPoNumber() {
      const poNumberSelection = this.poNumberSelection.data.map((selection) => selection.value)
      if (
        poNumberSelection.length < 1 ||
        (poNumberSelection.includes(1) && poNumberSelection.includes(0))
      ) {
        return null
      }
      return !!poNumberSelection.includes(1)
    },
    selectedCurrenciesObjects: {
      get() {
        return this.currenciesOptions.filter((currency) =>
          this.selectedCurrenciesCodes.data.includes(currency.value),
        )
      },
      set(newValue) {
        this.selectedCurrenciesCodes.data = newValue.map(
          (selectedCurrency) => selectedCurrency.value,
        )
      },
    },
    currenciesOptions() {
      return this.currencies.map((x) => ({ label: x, value: x }))
    },
  },
  watch: {
    filter: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.$emit('filters-updated', newVal)
      },
    },
  },
  methods: {
    $t,
    decodeInvoiceStatusOptionsFromUrl(data) {
      const selections = data?.split(',').filter(Boolean) || []
      return this.$options.invoiceStatusOptions.filter((option) =>
        selections.some((selection) => selection === option.value),
      )
    },
    decodeInvoiceTypeOptionsFromUrl(data) {
      const selections = data?.split(',').filter(Boolean) || []
      return this.$options.invoiceTypeOptions.filter((option) =>
        selections.some((selection) => selection === option.value),
      )
    },
    decodePoNumberOptionsFromUrl(data) {
      const selections = data?.split(',').filter(Boolean) || []
      return this.$options.poNumberOptions.filter((option) =>
        selections.some((selection) => parseInt(selection, 10) === option.value),
      )
    },
    resetFilters() {
      this.isResetting = true
      setTimeout(() => {
        this.isResetting = false
      }, 600)
      this.invoiceStatus.data = []
      this.invoiceType.data = []
      this.poNumberSelection.data = []
      this.selectedCurrenciesCodes.data = []
    },
  },
}
</script>

<style scoped>
.inline-block {
  display: inline-block !important;
}

@-webkit-keyframes rotation {
  from {
    transform: rotate(359deg);
  }
  to {
    transform: rotate(0deg);
  }
}

.fa-undo.loading {
  animation: rotation 0.3s 1 ease-out;
}
</style>
